import { useMemo } from 'react'

import { useGetTrackedGamePerformanceQuery } from '../../../api/core'
import { calculateEventPerformanceChanges, getEventKpiDataRange, utcDayAsLocalDate } from '../../live-events/helpers/calculateEventPerformanceChanges'
import { PerformanceChanges, TrackedGamePerformanceValue } from '../../live-events/types/TrackingEvents'
import { useCurrentMarket } from '../../markets'
import { BlueprintEventRow } from '../components/helpers/blueprintEventRows'

/** What an event's info popup needs to show its KPI chips. */
export interface BlueprintEventKpi {
  performanceChanges: PerformanceChanges
  /** The event's first UTC day, as a local midnight: the chip row formats dates in local time. */
  startTime: number
  /**
   * The end of the event's last UTC day, or the end of the same date in local time when that comes earlier, so the chip row
   * prints the UTC date in every zone and the tooltip's `end_time < now` gate opens no later than the UTC day ends.
   */
  endTime: number
}

const DAY_MS = 24 * 60 * 60 * 1000

const NO_KPIS: ReadonlyMap<string, BlueprintEventKpi> = new Map()

const utcDay = (timestamp: number) => new Date(timestamp).toISOString().slice(0, 10)

/**
 * The endpoint leaves out every day on which revenue, downloads and DAU are all 0, and a day with no data looks the same
 * as a day of zeros. Averages and day-over-day changes need those days as zeros: before = 100, 0, 0 against during = 50, 50, 50
 * is +50%, not -50% (only the non-zero days averaged). So every day the response omits between its first and last present day
 * becomes `revenue: 0, downloads: 0`, with DAU left absent (not 0), as the endpoint sends it.
 *
 * Days outside that span are NOT filled: the backend may simply not have them (the latest day is not computed yet, or a free
 * user's response covers only the free period), and a zero read from them would be a confident false -100%, Infinity or 0%.
 * Hidden beats a false Infinity, so an instance reading a day outside the span gets no KPIs (see `coverage`). A launch-week
 * event, whose comparison days lie before the game's first data, is therefore hidden too; showing Infinity for it would need a
 * real launch-date signal from the backend. An empty response (`{}`) has no span and shows no KPIs.
 */
const withZeroDays = (values: TrackedGamePerformanceValue[], from: number, to: number): TrackedGamePerformanceValue[] => {
  const present = new Set(values.map((value) => value.date))
  const filled = [...values]
  for (let day = from; day <= to; day += DAY_MS) {
    const date = utcDay(day)
    if (!present.has(date)) filled.push({ date, revenue: 0, downloads: 0 })
  }
  return filled
}

/** The first and last UTC day (as UTC midnights) the response has values for; `undefined` when it has none. */
const coverage = (values: TrackedGamePerformanceValue[]): { from: number; to: number } | undefined => {
  if (values.length === 0) return undefined
  const days = values.map((value) => Date.parse(value.date))
  return { from: Math.min(...days), to: Math.max(...days) }
}

interface UseBlueprintEventKpisParams {
  gameId: string
  /** Every instance of the blueprint, not only the filtered ones, so a filter change keeps the same request. */
  rows: BlueprintEventRow[]
  /** The blueprint block is open, the game is unlocked and it is a mobile game. */
  enabled: boolean
}

/**
 * KPI chip values of the event instances of one blueprint of one game, keyed by `BlueprintEventRow.key`.
 *
 * One `POST /games/tracked/performance` covers all of the blueprint's ended instances, however old: from the earliest day any of
 * them reads (the comparison days before it) to the last day any of them ends, in UTC days. It starts when `enabled` turns on,
 * i.e. when the block opens, so the data is in the cache before the first hover. An instance that is missing from the map has no
 * KPIs: it has not ended, the request failed (a PC/console or locked game answers 400, a backend without the endpoint 404), or
 * the data does not cover it. Nothing here blocks the popup.
 */
export const useBlueprintEventKpis = ({ gameId, rows, enabled }: UseBlueprintEventKpisParams): ReadonlyMap<string, BlueprintEventKpi> => {
  const { currentMarketIso } = useCurrentMarket()

  const plan = useMemo(() => {
    if (!enabled || !gameId) return undefined
    const now = Date.now()
    const ended = rows.flatMap((row) => {
      const range = getEventKpiDataRange({ eventStart: row.startDate, eventEnd: row.endDate, dayBasis: 'utc' }, now)
      return range ? [{ row, range }] : []
    })
    if (ended.length === 0) return undefined
    return {
      instances: ended.map(({ row }) => row),
      range: { from: Math.min(...ended.map(({ range }) => range.from)), to: Math.max(...ended.map(({ range }) => range.to)) },
    }
  }, [enabled, gameId, rows])

  // `currentData` rather than `data`: while the window moves to a new one, the previous window's data must not be read for it.
  const { currentData: performance } = useGetTrackedGamePerformanceQuery(
    { gameId, startTimestamp: plan?.range.from ?? 0, endTimestamp: plan?.range.to ?? 0, marketIso: currentMarketIso },
    { skip: !plan }
  )

  return useMemo(() => {
    if (!performance || !plan) return NO_KPIS
    const covered = coverage(performance.performanceValues)
    const performanceValues = covered ? withZeroDays(performance.performanceValues, covered.from, covered.to) : performance.performanceValues
    const kpis = new Map<string, BlueprintEventKpi>()
    plan.instances.forEach((row) => {
      const range = getEventKpiDataRange({ eventStart: row.startDate, eventEnd: row.endDate, dayBasis: 'utc' })
      if (!range || (covered && (range.from < covered.from || range.to > covered.to))) return
      const changes = calculateEventPerformanceChanges({
        eventStart: row.startDate,
        eventEnd: row.endDate,
        performanceChanges: performance.performanceChanges,
        performanceValues,
        dayBasis: 'utc',
      })
      if (!changes || Object.values(changes).every((value) => value === undefined)) return
      const utcEndOfLastDay = Math.floor(row.endDate / DAY_MS) * DAY_MS + DAY_MS - 1
      kpis.set(row.key, {
        performanceChanges: changes,
        startTime: utcDayAsLocalDate(row.startDate).getTime(),
        endTime: Math.min(utcDayAsLocalDate(row.endDate).setHours(23, 59, 59, 999), utcEndOfLastDay),
      })
    })
    return kpis
  }, [performance, plan])
}
